'use client';

import { useState } from 'react';
import s from './AgentIQ.module.css';

/* AgentIQ landing page — redesigned (Oct 2026).
   Copy/layout come from the approved design export; styles are page-local
   (AgentIQ.module.css) and do NOT touch shared tokens/globals.
   The site Navbar/Footer come from the root layout; the page ends with
   <BookDemoSection/> (id="book-demo"), which every "Talk to Customer Success" CTA
   scrolls to. Images live in /public/ai-stack/agentiq/v2/. */

const IMG = '/ai-stack/agentiq/v2';

const STATS = [
  ['40–60%', 'Fewer escalations from mishandled replies'],
  ['65%', 'Higher first-contact resolution'],
  ['100%', 'Of replies checked, not a 2% sample'],
  ['Real-time', 'Guidance as the agent types'],
];

const SIGNALS = [
  {
    title: 'Real-time, before it sends',
    body: 'Risky replies are caught the instant they are typed, not in a post-mortem next week. The prevention, not a report card.',
    img: 'signal-realtime.webp', w: 416, h: 195,
    alt: 'A reply being intercepted before send because risk was detected',
  },
  {
    title: 'Tone, empathy & compliance',
    body: 'Every message is checked for the right tone, real empathy and brand and policy rules, so you are never one bad reply away from a crisis.',
    img: 'signal-tone.webp', w: 422, h: 193,
    alt: 'Scores for tone, empathy and policy compliance on a single reply',
  },
  {
    title: 'No dropped balls',
    body: 'Detects open commitments and premature closures, so promises get kept, tickets get owned and nothing quietly slips through the cracks.',
    img: 'signal-commitments.webp', w: 421, h: 194,
    alt: 'Commitment tracker flagging a reply with no owner and no timeline',
  },
];

const STEPS = [
  ['01', 'Agent types a reply', "AgentIQ™ reads the draft in real time, in the context of the full conversation and the customer's sentiment."],
  ['02', 'Every risk gets flagged', 'Dismissive tone, policy breaches, missing empathy and open commitments are surfaced inline, with the reason for each flag.'],
  ['03', 'Reply gets a brand-safe score', 'A single score tells the agent how safe the response is to send, before the customer ever sees it.'],
  ['04', 'AI suggests the better version', 'One click rewrites the reply with the right empathy, clarity and policy language, so the fix takes seconds, not a coaching session.'],
];

const GUARDRAILS = [
  ['Tone & escalation risk', 'Spots dismissive, defensive or robotic language and prompts the agent to answer with warmth instead of deflection.'],
  ['Empathy & acknowledgement', "Detects when a frustrated customer's emotion has been ignored, and prompts a genuine acknowledgement before the fix."],
  ['Policy & brand compliance', 'Checks every reply against your brand and policy rules, so a disputed charge is never described in a way that misleads the customer.'],
  ['Commitments & next steps', 'Flags half-made promises and vague timelines, so every ticket leaves with a clear owner and a next step.'],
];

const OLD_WAY = [
  'Reviews 2 to 5% of conversations, weeks later',
  'Damage is done before the review begins',
  'Feedback lands as a scorecard, not a save',
  'Coaching improves the next ticket, not this one',
];

const NEW_WAY = [
  'Checks 100% of replies before they are sent',
  'Risky responses never reach the customer',
  'Feedback arrives as a better reply, one click away',
  'Every agent is coached live, on every conversation',
];

const USE_CASES = [
  {
    tag: 'Escalation prevention', title: 'Stop the viral screenshot',
    body: 'One dismissive reply is a public incident. AgentIQ™ intercepts it before the customer hits screenshot and share.',
    img: 'usecase-escalation.webp', w: 513, h: 259,
    alt: 'One dismissive agent reply multiplying into viral social posts and a falling sentiment chart',
  },
  {
    tag: 'Regulated industries', title: 'Keep every reply compliant',
    body: 'In BFSI, telecom and healthcare, a wrong sentence is a compliance risk. AgentIQ™ enforces policy rules in every message, automatically.',
    img: 'usecase-regulated.webp', w: 397, h: 121,
    alt: 'BFSI, telecom and healthcare industry arches',
  },
  {
    tag: 'Team ramp-up', title: 'Make new agents senior, faster',
    body: 'Every reply carries the judgement of your best agent, so new hires ramp on live conversations instead of role-play scenarios.',
    img: 'usecase-rampup.webp', w: 432, h: 123,
    alt: 'A new hire being coached live and becoming a senior agent',
  },
];

const FAQS = [
  ['What is AgentIQ™?', 'A real-time quality layer inside the response workflow. It reads every reply as the agent writes it, flags tone, empathy, policy and commitment risks, and offers a brand-safe rewrite before the message is sent.'],
  ['How is AgentIQ™ different from traditional QA reviews?', 'QA samples a small share of conversations weeks after the customer lived them. AgentIQ™ checks every reply before it is sent, so the fix happens on this ticket rather than the next one.'],
  ['What does AgentIQ™ check in every reply?', 'Tone and escalation risk, empathy and acknowledgement, policy and brand compliance, and open commitments with clear owners and timelines.'],
  ['Does AgentIQ™ improve handle time and CSAT?', 'Agents spend less time rewriting, escalating and recovering conversations that went wrong. Fewer escalations and higher first-contact resolution follow from getting the first reply right.'],
];

function Head({ eyebrow, title, lead }) {
  return (
    <div className={s.head}>
      <div className={s.eyebrow}>{eyebrow}</div>
      <h2 className={s.h2}>{title}</h2>
      {lead && <p className={s.lead}>{lead}</p>}
    </div>
  );
}

export default function AgentIQClient() {
  const [open, setOpen] = useState(0);

  return (
    <div className={s.root}>
      {/* Hero */}
      <section data-screen-label="Hero" className={s.hero}>
        <div className={s.heroGrid}>
          <div>
            <h1 className={s.h1}>A copilot on every agent&apos;s shoulder.</h1>
            <p className={s.heroSub}>AgentIQ™ reads every reply before it is sent, flagging risky tone, missed empathy, policy breaches and half-made promises in real time. The result: fewer escalations, faster resolutions, and a brand-safe response every single time.</p>
            <div className={s.btnRow}>
              <a href="#book-demo" className={`${s.btn} ${s.btnPrimary}`}>Talk to Customer Success</a>
              <a href="mailto:sales@locobuzz.com" className={`${s.btn} ${s.btnGhost}`}>sales@locobuzz.com</a>
            </div>
          </div>
          {/* Desktop: art is the section background. Tablet/phone: it drops below the copy. */}
          <img className={s.heroImg} src={`${IMG}/hero-bg.webp`} width={1674} height={850} decoding="async" alt="AgentIQ flagging a draft reply for dismissive tone, missed empathy and policy breaches" />
        </div>

        <div className={s.stats}>
          {STATS.map(([big, small]) => (
            <div key={big} className={s.stat}>
              <div className={s.statBig}>{big}</div>
              <div className={s.statSmall}>{small}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Signals, not keywords */}
      <section data-screen-label="Signals not keywords" className={s.section}>
        <div className={s.inner} style={{ maxWidth: 1400 }}>
          <Head
            eyebrow="Signals, not keywords"
            title="Every agent, performing like your best one."
            lead="AgentIQ™ sits inside the response workflow and guides every reply as it is written. Quality stops being a monthly review and becomes part of the moment of reply."
          />
          <div className={s.signals}>
            {SIGNALS.map((c) => (
              <div key={c.title} className={s.signalCard}>
                <h3 className={s.signalTitle}>{c.title}</h3>
                <p className={s.signalCopy}>{c.body}</p>
                <img className={s.signalImg} src={`${IMG}/${c.img}`} width={c.w} height={c.h} loading="lazy" decoding="async" alt={c.alt} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How AgentIQ works */}
      <section data-screen-label="How AgentIQ works" className={`${s.section} ${s.sectionAlt}`}>
        <Head eyebrow="How AgentIQ™ works" title={<>From draft to <span style={{ whiteSpace: 'nowrap' }}>brand-safe</span> reply in one flow.</>} />
        <div className={s.flowWrap}>
          <img className={s.flowImg} src={`${IMG}/flow.webp`} width={1761} height={656} loading="lazy" decoding="async" alt="An agent drafts a reply, AgentIQ flags the risks, scores it for brand safety, rewrites it in one click and the customer receives the improved answer" />
        </div>
        <div className={s.steps}>
          {STEPS.map(([n, title, body]) => (
            <div key={n} className={s.step}>
              <span className={s.stepNum}>{n}</span>
              <h3 className={s.stepTitle}>{title}</h3>
              <p className={s.stepCopy}>{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Quality guardrails */}
      <section data-screen-label="Quality guardrails" className={s.section}>
        <div className={s.inner}>
          <Head eyebrow="Built-in quality guardrails" title="What AgentIQ™ checks in every reply." />
          <div className={s.guard}>
            <img className={s.guardImg} src={`${IMG}/guardrails.webp`} width={909} height={544} loading="lazy" decoding="async" alt="A draft reply annotated with dismissive tone, missed empathy, policy breach and open commitment flags" />
            <div className={s.guardList}>
              {GUARDRAILS.map(([title, body]) => (
                <div key={title}>
                  <h3 className={s.guardTitle}>{title}</h3>
                  <p className={s.guardCopy}>{body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* QA, reinvented */}
      <section data-screen-label="QA reinvented" className={`${s.section} ${s.sectionAlt}`}>
        <div className={s.inner}>
          <Head
            eyebrow="QA, reinvented"
            title="Monthly QA reviews vs AgentIQ™."
            lead="Traditional quality assurance finds problems after the customer has already lived them. AgentIQ™ prevents them at the point of reply."
          />
          <div className={s.compare}>
            <div className={`${s.cmpCard} ${s.cmpOld}`}>
              <div className={s.cmpTag}>Keyword monitoring</div>
              <h3 className={s.cmpTitle}>Sampled, delayed, reactive</h3>
              {OLD_WAY.map((p) => <p key={p} className={s.cmpItem}>{p}</p>)}
            </div>
            <div className={`${s.cmpCard} ${s.cmpNew}`}>
              <div className={s.cmpTag}>AgentIQ™</div>
              <h3 className={s.cmpTitle}>Complete, instant, preventive</h3>
              {NEW_WAY.map((p) => <p key={p} className={s.cmpItem}>{p}</p>)}
            </div>
          </div>
        </div>
      </section>

      {/* Where teams use it */}
      <section data-screen-label="Where teams use it" className={s.section}>
        <div className={s.inner}>
          <Head eyebrow="Where teams use it" title="Built for the moments that make or break CX." />
          <div className={s.usecases}>
            {USE_CASES.map((c) => (
              <div key={c.title} className={s.ucCard}>
                <div className={s.ucMedia}>
                  <img className={s.ucImg} src={`${IMG}/${c.img}`} width={c.w} height={c.h} loading="lazy" decoding="async" alt={c.alt} />
                </div>
                <div className={s.ucTag}>{c.tag}</div>
                <h3 className={s.ucTitle}>{c.title}</h3>
                <p className={s.ucCopy}>{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Moment of reply */}
      <section data-screen-label="Moment of reply" className={`${s.section} ${s.sectionJoin}`}>
        <div className={s.head} style={{ marginBottom: 0, maxWidth: 900 }}>
          <h2 className={s.momentH2}>Quality is no longer a monthly review.<br />It is the moment of reply.</h2>
          <p className={s.lead} style={{ maxWidth: 680 }}>By shifting from post-mortem scorecards to real-time prevention, AgentIQ™ ensures fewer escalations, faster resolutions, and a brand-safe response every single time.</p>
        </div>
        <img className={s.momentImg} src={`${IMG}/moment.webp`} width={1046} height={297} loading="lazy" decoding="async" alt="A risk meter running from a high-risk score of 34 to a brand-safe score of 96" />
      </section>

      {/* FAQ */}
      <section data-screen-label="FAQ" className={`${s.section} ${s.sectionFlat}`}>
        <Head eyebrow="FAQ" title="Frequently asked questions." />
        <div className={s.faq}>
          {FAQS.map(([q, a], i) => {
            const isOpen = open === i;
            return (
              <div key={q} className={`${s.faqItem} ${isOpen ? s.faqOpen : ''}`}>
                <button type="button" className={s.faqBtn} aria-expanded={isOpen} aria-controls={`aiq-faq-${i}`} onClick={() => setOpen(isOpen ? -1 : i)}>
                  <span className={s.faqQ}>{q}</span>
                  <span aria-hidden="true" className={s.faqIcon}>{isOpen ? '−' : '+'}</span>
                </button>
                {/* Always in the DOM (hidden when closed) so the answers stay crawlable. */}
                <p id={`aiq-faq-${i}`} className={s.faqAns} hidden={!isOpen}>{a}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Final CTA */}
      <section data-screen-label="Final CTA" className={s.ctaSection}>
        <div className={s.ctaBox}>
          <h2 className={s.ctaH2}>Turn every agent into your best agent.</h2>
          <p className={s.ctaCopy}>See AgentIQ™ review a live reply, flag the risks, and rewrite it brand-safe in seconds. Book a walkthrough with our team and watch it work on real conversations.</p>
          <div className={s.ctaBtns}>
            <a href="#book-demo" className={`${s.btn} ${s.btnPrimary}`}>Talk to Customer Success</a>
            <a href="mailto:sales@locobuzz.com" className={`${s.btn} ${s.btnLight}`}>sales@locobuzz.com</a>
          </div>
        </div>
      </section>
    </div>
  );
}
